/**
 * Cart Page Module
 * 
 * Handles cart page interactions with server-side section rendering.
 * Coordinates with quantity-selector-component for quantity updates.
 * 
 * Features:
 * - Progressive enhancement (works without JS)
 * - Section rendering for updates (triggered by quantity-selector-component)
 * - Item removal with focus management
 * - Upsell add-to-cart
 * - Swiper initialization for upsell carousel
 * - ARIA live regions for accessibility
 * 
 * Performance:
 * - Minimal DOM updates (section replacement only)
 * - No external dependencies except Swiper
 */

class CartPageManager {
  constructor() {
    this.container = document.querySelector('[data-cart-page-section]');
    if (!this.container) return;

    this.sectionId = this.container.dataset.sectionId;
    this.statusElement = this.container.querySelector('[data-cart-status]');
    this.itemsContainer = this.container.querySelector('[data-cart-items-container]');
    
    this.init();
  }

  init() {
    this.bindCartUpdateEvents();
    this.bindRemoveButtons();
    this.bindUpsellForms();
    this.initializeUpsellSwiper();
    console.log('Cart page initialized');
  }

  /**
   * Bind cart update events
   * Listen for quantity changes from quantity-selector-component
   */
  bindCartUpdateEvents() {
    // Listen for cart updates from quantity-selector-component
    window.addEventListener('cart:updated', this.handleCartUpdated.bind(this));
  }

  /**
   * Bind remove item buttons
   */
  bindRemoveButtons() {
    const removeButtons = this.container.querySelectorAll('[data-cart-remove]');
    removeButtons.forEach(btn => {
      btn.addEventListener('click', this.handleRemoveItem.bind(this));
    });
  }

  /**
   * Bind upsell add to cart forms
   */
  bindUpsellForms() {
    const upsellForms = this.container.querySelectorAll('[data-cart-upsell-form]');
    upsellForms.forEach(form => {
      form.addEventListener('submit', this.handleUpsellAddToCart.bind(this));
    });
  }

  /**
   * Initialize upsell swiper carousel
   */
  initializeUpsellSwiper() {
    // Trigger swiper initialization event (handled by swiper.js)
    const event = new CustomEvent('cart:upsell-swiper-init', {
      detail: { container: this.container }
    });
    document.dispatchEvent(event);
  }

  /**
   * Handle remove item
   */
  async handleRemoveItem(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const lineKey = button.dataset.lineKey;
    const itemElement = button.closest('[data-cart-item]');
    
    // Store reference to next focusable element
    const nextFocusTarget = this.findNextFocusTarget(itemElement);
    
    try {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      
      // Remove item (quantity = 0)
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to remove item');
      }
      
      const cart = await response.json();
      
      // Re-render the cart section
      await this.renderCartSection();
      
      // Update ARIA status
      this.updateAriaStatus(cart, 'Item removed from cart');
      
      // Restore focus
      if (nextFocusTarget) {
        nextFocusTarget.focus();
      }
      
    } catch (error) {
      console.error('Error removing item:', error);
      button.disabled = false;
      button.removeAttribute('aria-busy');
      alert('Failed to remove item. Please try again.');
    }
  }

  /**
   * Handle upsell add to cart
   */
  async handleUpsellAddToCart(event) {
    event.preventDefault();
    
    const form = event.currentTarget;
    const button = form.querySelector('[data-upsell-add-button]');
    const variantId = form.querySelector('input[name="id"]').value;
    const quantity = parseInt(form.querySelector('input[name="quantity"]').value) || 1;
    
    // Collect any line item properties from hidden inputs
    const properties = {};
    form.querySelectorAll('input[name^="properties["]').forEach(input => {
      const match = input.name.match(/properties\[(.*)\]/);
      if (match) {
        properties[match[1]] = input.value;
      }
    });
    
    const originalText = button.textContent;
    
    try {
      button.disabled = true;
      button.textContent = 'Adding...';
      
      const requestBody = {
        id: variantId,
        quantity: quantity
      };
      
      // Only add properties if there are any
      if (Object.keys(properties).length > 0) {
        requestBody.properties = properties;
      }
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error('Failed to add item');
      }
      
      const item = await response.json();
      
      // Re-render cart section
      await this.renderCartSection();
      
      // Update ARIA status
      this.updateAriaStatus(null, `${item.product_title} added to cart`);
      
      // Success feedback
      button.textContent = 'Added!';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        // Reset quantity to 1
        form.querySelector('input[name="quantity"]').value = 1;
      }, 1000);
      
    } catch (error) {
      console.error('Error adding upsell item:', error);
      button.textContent = originalText;
      button.disabled = false;
      alert('Failed to add item. Please try again.');
    }
  }

  /**
   * Render cart section using Shopify section rendering
   */
  async renderCartSection() {
    try {
      const response = await fetch(`/?section_id=cart-template`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch cart section');
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract the cart section
      const newSection = doc.querySelector('[data-cart-page-section]');
      
      if (newSection && this.container) {
        // Replace the entire section
        this.container.innerHTML = newSection.innerHTML;
        
        // Re-initialize event listeners
        this.statusElement = this.container.querySelector('[data-cart-status]');
        this.itemsContainer = this.container.querySelector('[data-cart-items-container]');
        
        this.bindRemoveButtons();
        this.bindUpsellForms();
        this.initializeUpsellSwiper();
      }
      
    } catch (error) {
      console.error('Error rendering cart section:', error);
      // Fallback: reload page
      window.location.reload();
    }
  }

  /**
   * Handle cart updated event (from quantity-selector-component)
   */
  async handleCartUpdated(event) {
    const cart = event.detail;
    
    // Re-render cart section
    await this.renderCartSection();
    
    // Update ARIA status
    this.updateAriaStatus(cart);
  }

  /**
   * Update ARIA live region status
   */
  updateAriaStatus(cart, customMessage = null) {
    if (!this.statusElement) return;
    
    if (customMessage) {
      this.statusElement.textContent = customMessage;
      return;
    }
    
    if (cart) {
      if (cart.item_count === 0) {
        this.statusElement.textContent = 'Cart is empty';
      } else {
        const itemText = cart.item_count === 1 ? 'item' : 'items';
        const totalPrice = this.formatMoney(cart.total_price);
        this.statusElement.textContent = `Cart updated: ${cart.item_count} ${itemText}, total ${totalPrice}`;
      }
    }
  }

  /**
   * Find next focusable element after removed item
   */
  findNextFocusTarget(itemElement) {
    // Try next item
    const nextItem = itemElement.nextElementSibling;
    if (nextItem) {
      return nextItem.querySelector('quantity-selector-component input, [data-cart-remove]');
    }
    
    // Try previous item
    const prevItem = itemElement.previousElementSibling;
    if (prevItem) {
      return prevItem.querySelector('quantity-selector-component input, [data-cart-remove]');
    }
    
    // Fallback to checkout button
    return document.querySelector('.cart-summary__checkout');
  }

  /**
   * Format money (basic implementation)
   * In production, use Shopify's money formatting
   */
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `£${amount}`;
  }
}

// Initialize cart page when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartPageManager();
  });
} else {
  new CartPageManager();
}

// Re-initialize on Shopify section load (theme editor)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-cart-page-section]')) {
    new CartPageManager();
  }
});
